/**
 * Derives the automation engine origin from the host the app is currently
 * served from. This lets a single pre-built bundle (the `docker` platform image,
 * served at app.famly.test locally and app.famly.{co,de}/whitelabel hosts in
 * production) talk to the matching automation engine deployment without baking
 * an absolute URL at build time — mirroring how the host-relative `/api` path
 * resolves against whatever host serves the page.
 *
 * The leading `app.` label is swapped for `automation-engine.`; hosts that don't
 * start with `app.` (e.g. the `familyapp.brighthorizons.co.uk` whitelabel) get
 * `automation-engine.` prepended.
 *
 * @param hostname - The current host without port (e.g. `window.location.hostname`)
 * @param protocol - The current protocol incl. trailing colon (e.g. `window.location.protocol`, `'https:'`)
 * @returns The automation engine origin, e.g. `https://automation-engine.famly.co`
 *
 * @example
 * deriveAutomationEngineUrl('app.famly.test', 'http:'); // 'http://automation-engine.famly.test'
 * deriveAutomationEngineUrl('app.famly.co', 'https:');   // 'https://automation-engine.famly.co'
 * deriveAutomationEngineUrl('familyapp.brighthorizons.co.uk', 'https:'); // 'https://automation-engine.familyapp.brighthorizons.co.uk'
 */
export const deriveAutomationEngineUrl = (hostname: string, protocol: string): string => {
    const appPrefix = 'app.';
    const baseHost = hostname.startsWith(appPrefix) ? hostname.slice(appPrefix.length) : hostname;
    return `${protocol}//automation-engine.${baseHost}`;
};
